import { useMemo } from 'react';
import { useOfferBuilderContext } from '@/enterprise/components/OfferBuilder/OfferBuilderContext';
import useGroupByBuyer, { BuyerGroupWithStats } from '@/enterprise/data/useGroupByBuyer';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';

/**
 * Sorts buyers and taker markets based on the initial display order.
 */
const useStableGroupByBuyer = ({ takerMarkets }: { takerMarkets: TakerMarket[] }): BuyerGroupWithStats[] => {
  const { state } = useOfferBuilderContext();
  const { initialDisplayOrder } = state;
  const unsortedBuyers = useGroupByBuyer({ takerMarkets, sortByEligible: true });

  return useMemo(() => {
    if (!initialDisplayOrder) {
      return unsortedBuyers;
    }

    const { buyers: buyerOrder, takerMarketsPerBuyer } = initialDisplayOrder;

    // create lookup for preserved order
    const buyerOrderMap = Object.fromEntries(buyerOrder.map((uuid, idx) => [uuid, idx]));

    // sort buyers by preserved order
    const sortedBuyers = [...unsortedBuyers].sort((a, b) => {
      const aOrder = buyerOrderMap[a.makerOrganizationUuid] ?? Infinity;
      const bOrder = buyerOrderMap[b.makerOrganizationUuid] ?? Infinity;
      return aOrder - bOrder;
    });

    // sort takerMarkets within each buyer by preserved order
    return sortedBuyers.map((buyer) => {
      const buyerTakerMarketOrder = takerMarketsPerBuyer[buyer.makerOrganizationUuid] || [];
      const tmOrderMap = Object.fromEntries(buyerTakerMarketOrder.map((id, idx) => [id, idx]));

      return {
        ...buyer,
        takerMarkets: [...buyer.takerMarkets].sort((a, b) => {
          const aOrder = tmOrderMap[a.id] ?? Infinity;
          const bOrder = tmOrderMap[b.id] ?? Infinity;
          return aOrder - bOrder;
        }),
      };
    });
  }, [unsortedBuyers, initialDisplayOrder]);
};

export default useStableGroupByBuyer;
